import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { List, Divider } from 'antd';

import arrowDown from "../../images/arrowDown.svg";
import paperPlaneSend from "../../images/paperPlane.svg";
import { getMessagesDate, getOnlyTime } from "../../utils/DateTimeHandler";
import { Message } from "./Message";
import { Message as MessageProps } from "../../types";

const ChatContainer = styled.div<{mobileVisible: boolean}>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 320px;

  .ant-list-empty-text {
    margin-top: 40%;
  }

  @media (max-width: 640px) {
    display: ${props => props.mobileVisible ? 'flex' : 'none'};
    width: 100%;
  }
`;

const MessageInput = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  border-top: 1px solid rgb(209, 214, 220);
  padding: 12px 16px;
  background-color: #FFF;
  position: sticky;
  bottom: 0;

  input[type="text"] {
    border: none;
    width: 100%;

    &::placeholder {
      color: rgb(140, 153, 166);
    }
  }

  img {
    cursor: pointer;
  }
`;

const ScrollIcon = styled.div<{visible: boolean}>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #D1D6DC;
  box-shadow: 0px 1px 4px 0px #00000014;
  background-color: #FFFFFF;
  cursor: pointer;
  position: fixed;
  z-index: 999;
  right: 20px;
  bottom: 80px;
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;

  img {
    width: 14px;
    height: 14px;
  }
`;

type ActiveDialogProps = {
  messageList: MessageProps[];
  lastReadMessageId: number;
  onSendMessage: (message: string) => void;
  activeDialogId: number;
  onRead: (messageId: number, unreadMessagesCount: number) => void;
  dialogVisible: boolean;
}

export const ActiveDialog = ({ messageList, lastReadMessageId, onSendMessage, activeDialogId, onRead, dialogVisible } : ActiveDialogProps) => {
  const [message, setMessage] = useState('');
  const [readMessages, setReadMessages] = useState<MessageProps[]>([]);
  const [unreadMessages, setUnreadMessages] = useState<MessageProps[]>([]);
  const [isScrolledEnough, setIsScrolledEnough] = useState(false);

  const messageEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const lastReadMessage = messageList.find(message => message.id === lastReadMessageId);
    const lastReadMessageInd = lastReadMessage ? messageList.indexOf(lastReadMessage) : -1;

    if (lastReadMessageInd !== -1) {
      setReadMessages(messageList.slice(0, lastReadMessageInd + 1));
      setUnreadMessages(messageList.slice(lastReadMessageInd + 1));
    } else {
      setReadMessages(messageList);
      setUnreadMessages([]);
    }

    scrollToBottom();
  }, [messageList, lastReadMessageId]);

  useEffect(() => {
    scrollToBottom();
  }, [messageList, readMessages, unreadMessages]);

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: 'auto' });
  };

  const sendMessage = () => {
    if (message.trim()) {
        setReadMessages([...readMessages, ...unreadMessages]);
        setUnreadMessages([]);
        onSendMessage(message);
        setMessage('');
        scrollToBottom();
    }
  };

  const handleRead = () => {
    const lastMessage = unreadMessages.at(-1);
    if (lastMessage) {
      onRead(lastMessage.id, 0);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.code === 'Enter' && !message.match(/^ *$/)) {
      sendMessage();
    }
  };

  const handleScroll = (e: React.UIEvent) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight < scrollHeight - 100) {
      setIsScrolledEnough(true);
    } else {
      setIsScrolledEnough(false);
    }
    
    const allMessagesPosition = document.getElementById("dialogMessages")?.getBoundingClientRect().bottom;
    const unreadMessagesPosition = document.getElementById("unreadMessages")?.getBoundingClientRect().bottom;
    
    if (unreadMessagesPosition === undefined || allMessagesPosition === undefined) {
      return;
    }

    if (unreadMessagesPosition - allMessagesPosition < 30) {
      handleRead();
    }
  };

  const locale = {
    emptyText: "Выберите чат, чтобы написать сообщение"
  }

  return (
    <ChatContainer mobileVisible={dialogVisible}>
      <div id="dialogMessages" onScroll={handleScroll} className="scrollbar-light" style={{ backgroundColor: '#fff', overflowY: 'auto', flex: 1 }}>
        {(readMessages) &&
          <>
              <List 
                  locale={locale}
                  dataSource={readMessages}
                  style={{padding: '16px', position: 'relative'}}
                  renderItem={({id, text, /* sender */ dateTime, isMine, links}, index) => (
                      <>
                          {(index === 0 || getMessagesDate(readMessages[index-1].dateTime) !== getMessagesDate(dateTime)) && (
                              <Divider>
                                  <p style={{fontWeight: '600', fontSize: '12px'}}>
                                      {getMessagesDate(dateTime)}
                                  </p>
                              </Divider>
                          )}
                          <Message 
                              id={id}
                              key={id}
                              text={text} 
                              // sender={sender} 
                              dateTime={getOnlyTime(dateTime)} 
                              isMine={isMine}
                              links={links}
                          />
                      </>
                  )}
              />
          </>}
          {(unreadMessages.length !== 0) && (
              <>
                  {unreadMessages.some(message => !message.isMine) &&
                    <div style={{backgroundColor: 'rgba(245, 245, 245, 1)', textAlign: 'center', padding: '4px'}}>
                        <p>Непрочитанные сообщения</p>
                    </div>
                  }
                  <List 
                      id="unreadMessages"
                      dataSource={unreadMessages}
                      style={{overflow: 'auto', padding: '16px'}}
                      renderItem={({id, text, /* sender */ dateTime, isMine, links}, index) => (
                          <>
                          {(index === 0 || getMessagesDate(unreadMessages[index-1].dateTime) !== getMessagesDate(dateTime)) && (
                              <Divider>
                                  <p style={{fontWeight: '600', fontSize: '12px'}}>
                                      {getMessagesDate(dateTime)}
                                  </p>
                              </Divider>
                          )}
                          <Message 
                              id={id}
                              key={id}
                              text={text} 
                              // sender={sender} 
                              dateTime={getOnlyTime(dateTime)} 
                              isMine={isMine}
                              links={links}
                          />
                          </>
                      )}
                  />
              </>
          )}
        <div ref={messageEndRef}></div>
        <ScrollIcon onClick={scrollToBottom} visible={isScrolledEnough}>
            <img src={arrowDown} alt='Scroll down'></img>
        </ScrollIcon>
      </div>
      {activeDialogId !== -1 && (
        /* <label htmlFor="input-file-upload">
            <img src={paperClipUpload}></img>
            <input 
                type="file" 
                id="input-file-upload" 
                style={{display: 'none'}} 
            />
        </label> */
        <MessageInput>
          <input
            type="text"
            placeholder="Введите сообщение"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={onKeyDown}
          />
          {message.trim() && (
            <img src={paperPlaneSend} onClick={sendMessage} alt='Send message'></img>
          )}
        </MessageInput>
      )}
    </ChatContainer>
  );
}